import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { MobileSidebarProvider } from "@/contexts/MobileSidebarContext";
import Login from "@/pages/Login";
import ProjectDashboard from "@/pages/ProjectDashboard";
import Dashboard from "@/pages/Dashboard";
import Search from "@/pages/Search";
import Admin from "@/pages/Admin";
import Shares from "@/pages/Shares";
import ClipRequestsAdmin from "@/pages/ClipRequestsAdmin";
import ShareView from "@/pages/ShareView";
import Favorites from "@/pages/Favorites";
import Collections from "@/pages/Collections";
import AssetChecklist from "@/pages/AssetChecklist";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Where a non-admin gets bounced when they hit an admin-only route.
// Kept in sync with Login.jsx homePath so redirects land in the expected spot.
function fallbackHome(user) {
  if (user?.role === "client" && user?.client_scope_folder_id) {
    return `/folders/${user.client_scope_folder_id}`;
  }
  if (user?.role === "editor") return "/library";
  return "/";
}

// Iter 27 role gate. Wraps a route; if the logged-in user's role isn't in
// `allow`, redirects them to their landing page instead of rendering.
// `allow` defaults to admin-only — that's the strict lock for global pages.
function RoleGated({ allow = ["admin"], children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) return <Navigate to={fallbackHome(user)} replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandProvider>
          <FavoritesProvider>
            <MobileSidebarProvider>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/s/:slug" element={<ShareView />} />
            {/* Admin-only landing pages */}
            <Route path="/" element={<RoleGated allow={["admin"]}><ProjectDashboard /></RoleGated>} />
            <Route path="/favorites" element={<RoleGated allow={["admin"]}><Favorites /></RoleGated>} />
            <Route path="/collections" element={<RoleGated allow={["admin", "editor", "client"]}><Collections /></RoleGated>} />
            <Route path="/collections/:cid" element={<RoleGated allow={["admin", "editor", "client"]}><Collections /></RoleGated>} />
            <Route path="/shares" element={<RoleGated allow={["admin"]}><Shares /></RoleGated>} />
            <Route path="/clip-requests" element={<RoleGated allow={["admin", "editor"]}><ClipRequestsAdmin /></RoleGated>} />
            <Route path="/clip-requests/:cid" element={<RoleGated allow={["admin", "editor"]}><ClipRequestsAdmin /></RoleGated>} />
            <Route path="/admin" element={<RoleGated allow={["admin"]}><Admin /></RoleGated>} />
            {/* Admin + editor cross-folder tooling */}
            <Route path="/library" element={<RoleGated allow={["admin", "editor"]}><Dashboard /></RoleGated>} />
            <Route path="/search" element={<RoleGated allow={["admin", "editor", "client"]}><Search /></RoleGated>} />
            {/* Everyone-with-login can drill into folders (backend scopes them) */}
            <Route path="/folders/:folderId" element={<Protected><Dashboard /></Protected>} />
            <Route path="/assets/:assetId/checklist" element={<Protected><AssetChecklist /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
            </MobileSidebarProvider>
          </FavoritesProvider>
        </BrandProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
